/**
 * Invoice Status Configuration
 * MUST match backend: customer-invoices/utils/statusConfig.js
 *
 * 6-state model: Draft → Sent → Partially_Paid → Paid (or Credited/Cancelled)
 * Overdue is now a computed flag (is_overdue), not a status.
 */

export const INVOICE_STATUSES = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  PARTIALLY_PAID: 'Partially_Paid',
  PAID: 'Paid',
  CANCELLED: 'Cancelled',
  CREDITED: 'Credited'
};

/**
 * Status colors for badges
 * Using Bootstrap color variants
 */
export const STATUS_COLORS = {
  [INVOICE_STATUSES.DRAFT]: 'secondary',           // Gray
  [INVOICE_STATUSES.SENT]: 'primary',              // Blue
  [INVOICE_STATUSES.PARTIALLY_PAID]: 'warning',    // Orange
  [INVOICE_STATUSES.PAID]: 'success',              // Green
  [INVOICE_STATUSES.CANCELLED]: 'secondary',       // Gray
  [INVOICE_STATUSES.CREDITED]: 'info'              // Purple/Blue
};

/**
 * Human-readable status labels
 */
export const STATUS_LABELS = {
  [INVOICE_STATUSES.DRAFT]: 'Utkast',
  [INVOICE_STATUSES.SENT]: 'Skickad',
  [INVOICE_STATUSES.PARTIALLY_PAID]: 'Delvis betald',
  [INVOICE_STATUSES.PAID]: 'Betald',
  [INVOICE_STATUSES.CANCELLED]: 'Makulerad',
  [INVOICE_STATUSES.CREDITED]: 'Krediterad'
};

/**
 * Get color for a status (for Badge component)
 * @param {string} status - Invoice status
 * @returns {string} Bootstrap color variant
 */
export const getStatusColor = (status) => {
  return STATUS_COLORS[status] || 'secondary';
};

/**
 * Get display label for a status
 * @param {string} status - Invoice status
 * @returns {string} Human-readable label
 */
export const getStatusLabel = (status) => {
  return STATUS_LABELS[status] || status;
};

/**
 * Get all statuses as array
 * @returns {Array<string>} All status values
 */
export const getAllStatuses = () => {
  return Object.values(INVOICE_STATUSES);
};

/**
 * Check if a status is valid
 * @param {string} status - Status to validate
 * @returns {boolean} True if valid
 */
export const isValidStatus = (status) => {
  return Object.values(INVOICE_STATUSES).includes(status);
};

/**
 * Status transitions (what statuses can change to)
 * Matches backend statusConfig.js
 */
export const STATUS_TRANSITIONS = {
  [INVOICE_STATUSES.DRAFT]: [
    INVOICE_STATUSES.SENT,
    INVOICE_STATUSES.CANCELLED
  ],
  [INVOICE_STATUSES.SENT]: [
    INVOICE_STATUSES.PARTIALLY_PAID,
    INVOICE_STATUSES.PAID,
    INVOICE_STATUSES.CANCELLED,
    INVOICE_STATUSES.CREDITED
  ],
  [INVOICE_STATUSES.PARTIALLY_PAID]: [
    INVOICE_STATUSES.PAID,
    INVOICE_STATUSES.CREDITED
  ],
  [INVOICE_STATUSES.PAID]: [
    INVOICE_STATUSES.CREDITED
  ],
  [INVOICE_STATUSES.CANCELLED]: [],
  [INVOICE_STATUSES.CREDITED]: []
};

/**
 * Get allowed transitions for a status
 * @param {string} status - Current status
 * @returns {Array<string>} Allowed next statuses
 */
export const getAllowedTransitions = (status) => {
  return STATUS_TRANSITIONS[status] || [];
};
